import React, { useEffect } from 'react';
import { Form, Input, Modal, Select, message } from 'antd';
import { useCreateBusinessAddress, useUpdateBusinessAddress } from '~/services/business-addresses';
import { TBusinessAddress, TCreateBusinessAddress, AddressType } from '~/types/business-address';

interface BusinessAddressModalProps {
  open: boolean;
  onCancel: () => void;
  businessAddress?: TBusinessAddress | null;
  onSuccess: () => void;
}

const addressTypeOptions = [
  { label: 'Warehouse', value: AddressType.WAREHOUSE },
  { label: 'Office', value: AddressType.OFFICE },
  { label: 'Storage', value: AddressType.STORAGE },
];

export const BusinessAddressModal: React.FC<BusinessAddressModalProps> = ({
  open,
  onCancel,
  businessAddress,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const createBusinessAddress = useCreateBusinessAddress();
  const updateBusinessAddress = useUpdateBusinessAddress();

  const isEditing = !!businessAddress;

  useEffect(() => {
    if (open) {
      if (businessAddress) {
        form.setFieldsValue({
          name: businessAddress.name,
          addressType: businessAddress.addressType,
          address: businessAddress.address,
          address2: businessAddress.address2,
          city: businessAddress.city,
          state: businessAddress.state,
          zip: businessAddress.zip,
          phone: businessAddress.phone,
          contactPerson: businessAddress.contactPerson,
          notes: businessAddress.notes,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, businessAddress, form]);

  const handleSubmit = async (values: TCreateBusinessAddress) => {
    try {
      if (isEditing && businessAddress) {
        await updateBusinessAddress.mutateAsync({
          id: businessAddress.id,
          data: values,
        });
        message.success('Business address updated successfully');
      } else {
        await createBusinessAddress.mutateAsync(values);
        message.success('Business address created successfully');
      }
      onSuccess();
      onCancel();
    } catch {
      message.error(`Failed to ${isEditing ? 'update' : 'create'} business address`);
    }
  };

  return (
    <Modal
      title={isEditing ? 'Edit Business Address' : 'Add New Business Address'}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={createBusinessAddress.isPending || updateBusinessAddress.isPending}
      width={700}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="mt-4"
        initialValues={{ addressType: AddressType.WAREHOUSE }}
      >
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="name"
            label="Location Name"
            rules={[{ required: true, message: 'Please enter location name' }]}
          >
            <Input placeholder="e.g., Main Warehouse" />
          </Form.Item>

          <Form.Item
            name="addressType"
            label="Address Type"
            rules={[{ required: true, message: 'Please select address type' }]}
          >
            <Select
              placeholder="Select type"
              options={addressTypeOptions}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="address"
          label="Address"
          rules={[{ required: true, message: 'Please enter address' }]}
        >
          <Input placeholder="Street address" />
        </Form.Item>

        <Form.Item
          name="address2"
          label="Address 2 (Optional)"
        >
          <Input placeholder="Apt, suite, unit, etc." />
        </Form.Item>

        <div className="grid grid-cols-3 gap-4">
          <Form.Item
            name="city"
            label="City"
            rules={[{ required: true, message: 'Please enter city' }]}
          >
            <Input placeholder="City" />
          </Form.Item>

          <Form.Item
            name="state"
            label="State"
            rules={[{ required: true, message: 'Please enter state' }]}
          >
            <Input placeholder="State" maxLength={2} />
          </Form.Item>

          <Form.Item
            name="zip"
            label="ZIP Code"
            rules={[{ required: true, message: 'Please enter ZIP code' }]}
          >
            <Input placeholder="ZIP" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="contactPerson"
            label="Contact Person"
          >
            <Input placeholder="Contact name" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
          >
            <Input placeholder="Phone number" />
          </Form.Item>
        </div>

        <Form.Item
          name="notes"
          label="Notes"
        >
          <Input.TextArea
            rows={3}
            placeholder="Additional notes about this location"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
